/* eslint-disable no-await-in-loop */
/* eslint-disable no-continue */
import {
  EnvironmentInjector,
  inject,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import { MaybeAsync, Params } from '@angular/router';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  ActivatorGuard,
  HeaderNavigation,
} from '../header-navigation/header-navigation';
import { Navigation } from './navigation-record.types';

@Injectable()
export class NavigationRecordService {
  private readonly environmentInjector = inject(EnvironmentInjector);

  compileNavigation(navigation: HeaderNavigation): Observable<Navigation> {
    if (navigation.length === 0) {
      return of([]);
    }

    return combineLatest(
      navigation.map((record) =>
        this.mapNavigationRecord(record, { level: 0, parent: null }),
      ),
    ).pipe(
      map((results) =>
        results.filter((result): result is Navigation.Record =>
          Boolean(result),
        ),
      ),
    );
  }

  private mapNavigationRecord(
    record: HeaderNavigation.MaybeLoaded,
    meta: { level: number; parent: Navigation.Group | null },
  ): Observable<Navigation.Record | null> {
    if (typeof record === 'function') {
      type LoaderResult = HeaderNavigation.Item | HeaderNavigation.Group | null;
      let loaderResult: Observable<LoaderResult>;

      try {
        runInInjectionContext(this.environmentInjector, () => {
          const result = record();
          if (result instanceof Observable) {
            loaderResult = result;
          } else if (result instanceof Promise) {
            loaderResult = from(result);
          } else {
            loaderResult = of(result);
          }
        });

        return loaderResult.pipe(
          switchMap((result) => this.mapNavigationRecord(result, meta)),
        );
      } catch (error) {
        // If the loader throws an error, return null
        return of(null);
      }
    }

    // Handle null records returned by loaders
    if (record === null) {
      return of(null);
    }

    const navRecordCommon = this.toNavigationCommonFields(record, meta);

    const recordGuardsResult = this.compileGuardActivators(navRecordCommon);

    const recordChildren: Observable<Navigation.Record | null>[] = [];
    if ('children' in record && record.children.length > 0) {
      const navRecordGroup = this.toNavRecordGroup(navRecordCommon, {
        children: [],
      });

      recordChildren.push(
        ...record.children.map((child) =>
          this.mapNavigationRecord(child, {
            parent: navRecordGroup,
            level: meta.level + 1,
          }),
        ),
      );

      return combineLatest([
        recordGuardsResult,
        combineLatest(recordChildren).pipe(
          map((results) => results.filter(Boolean)),
        ),
      ]).pipe(
        map(([guardResults, childrenResults]) => {
          navRecordGroup.children = childrenResults;

          if (!guardResults) {
            return null;
          }

          if (!childrenResults.length) {
            return null;
          }

          return navRecordGroup;
        }),
      );
    }

    if ('pathname' in record) {
      const navRecordItem = this.toNavRecordItem(navRecordCommon, {
        pathname: record.pathname ?? '',
        params: record.params ?? {},
      });

      return recordGuardsResult.pipe(
        map((guardResult) => {
          if (!guardResult) {
            return null;
          }

          return navRecordItem;
        }),
      );
    }

    return of(null);
  }

  private compileGuardActivators(
    record: Navigation.CommonFields,
  ): Observable<boolean> {
    const activators = (record.$meta.activators ?? [])
      .filter((guard) => typeof guard === 'function')
      .map((guard: ActivatorGuard) => {
        let result: MaybeAsync<boolean>;
        try {
          runInInjectionContext(this.environmentInjector, () => {
            result = guard();
          });

          if (result instanceof Observable) return result;
          if (result instanceof Promise) return from(result);
          return of(typeof result === 'boolean' ? result : true);
        } catch (error) {
          // If the guard throws an error, return false
          return of(false);
        }
      });

    if (activators.length < 1) {
      return of(true);
    }

    return combineLatest(activators).pipe(
      map((results) => results.every(Boolean)),
    );
  }

  private toNavigationCommonFields(
    record: HeaderNavigation.Record,
    meta: { level: number; parent: Navigation.Group | null },
  ): Navigation.CommonFields {
    return {
      $meta: {
        activators: record.activators ?? [],
        isRootRecord: meta.parent === null,
        parent: meta.parent ?? null,
        level: meta.level,
      },
      ariaLabel: record.ariaLabel,
      alias: record.alias,
      prefixIcon: record.prefixIcon,
      isSvg: record.isSvg,
      label:
        typeof record.label === 'string' ? record.label : record.label.text,
    };
  }

  private toNavRecordGroup(
    record: Navigation.CommonFields,
    props: { children: Navigation },
  ): Navigation.Group {
    const navRecordGroup: Navigation.Group = {
      ...record,
      $meta: {
        ...record.$meta,
        type: 'Group',
        get hasChildren() {
          return navRecordGroup.children.length > 0;
        },
      },
      children: props.children,
    };

    return navRecordGroup;
  }

  private toNavRecordItem(
    record: Navigation.CommonFields,
    props: { pathname: string; params: Params },
  ): Navigation.Item {
    const navRecordItem: Navigation.Item = {
      ...record,
      $meta: {
        ...record.$meta,
        hasChildren: false,
        type: 'Item',
      },
      pathname: props.pathname ?? '',
      params: props.params ?? {},
    };

    return navRecordItem;
  }
}
